import React from "react";
import { TransformedProduct } from "../../types/productApiTypes";

interface ProductPriceDisplayProps {
  product: TransformedProduct;
  /**
   * Optional: If a variation is actively selected (e.g., on the Single Product page), pass it here.
   * If provided, the component will display this specific variation's price instead of the parent's.
   */
  selectedVariation?: TransformedProduct | null;
  className?: string;
}

/**
 * Renders the appropriate price display for a product or variation.
 * Handles standard prices, sale prices (with strikethrough), and price ranges.
 * Includes headless UI configuration support to adjust behavior based on the active theme.
 */
const ProductPriceDisplay: React.FC<ProductPriceDisplayProps> = ({
  product,
  selectedVariation,
  className = "",
}) => {
  // Determine which data source to use for pricing (Variation takes precedence over Parent)
  const displayData = selectedVariation || product;
  const isOnSale = displayData.onSale;

  // Safety check: If the formatted price is empty or invalid, render nothing to avoid UI artifacts.
  if (!displayData.formattedPrice) {
    return null;
  }

  // --- 1. Global UI Configuration & Context Checks ---

  // Check if the active theme prefers showing a single default price instead of a min-max range on product cards.
  const preferDefaultPrice =
    window.commonVariables?.uiSettings?.productCard
      ?.preferDefaultPriceOverRange || false;

  // Determine the rendering context by checking the injected class names.
  // We only want to apply the range bypass logic on overview cards/panels, preserving the range on the single product page.
  const isCardContext =
    className.includes("product-card__price") ||
    className.includes("product-panel-item__price");

  // Evaluate the bypass flag:
  // The theme setting must be enabled, we must be in a card context,
  // AND the product MUST have a valid default variation to fall back to.
  const bypassRange =
    preferDefaultPrice && isCardContext && product.hasDefaultVariation;

  // --- 2. Price Range Logic ---

  // Show a price range (e.g., "10₺ – 50₺") ONLY if:
  // A) No specific variation is actively selected.
  // B) The product type inherently supports ranges (variable or grouped).
  // C) The backend provided valid min/max formatted prices.
  // D) The theme has NOT explicitly requested to bypass the range view for this context.
  if (
    !selectedVariation &&
    (product.type === "grouped" || product.isVariable) &&
    product.formattedMinPrice &&
    product.formattedMaxPrice &&
    !bypassRange
  ) {
    return (
      <div className={`product-price ${className}`}>
        <span className="product-price__current">
          {product.formattedMinPrice} – {product.formattedMaxPrice}
        </span>
      </div>
    );
  }

  // --- 3. Sale Price Logic ---

  // Displays both the original crossed-out regular price and the discounted current sale price.
  if (isOnSale) {
    return (
      <div className={`product-price product-price--on-sale ${className}`}>
        <del aria-label="Original price" className="product-price__regular">
          {displayData.formattedRegularPrice}
        </del>
        <ins aria-label="Sale price" className="product-price__current">
          {displayData.formattedSalePrice}
        </ins>
      </div>
    );
  }

  // --- 4. Standard Price Logic ---

  // Fallback for simple products, or specific variations that are not on sale.
  // If 'bypassRange' was true, this will naturally display the default variation's standard price.
  return (
    <div className={`product-price ${className}`}>
      <span className="product-price__current">
        {displayData.formattedPrice}
      </span>
    </div>
  );
};

export default ProductPriceDisplay;
